import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import CustomToolbar from "../components/CustomToolbar";
import Pagination from "../components/Pagination";
import LoadingSpinner from "../components/LoadingSpinner";
import "../styles/components.css";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";
import Swal from "sweetalert2";
import axios from "axios";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { format, parseISO, isValid } from "date-fns";

function EmployeeManagement() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [pageSize] = useState(10);
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [filterAnchor, setFilterAnchor] = useState(null);
    const [filtersInitialized, setFiltersInitialized] = useState(false);

    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [resetLoading, setResetLoading] = useState(false);

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");

    const { t } = useTranslation();
    const { user, token } = useAuth();
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const gridApiRef = useGridApiRef();

    useEffect(() => {
        const savedFilters = localStorage.getItem("employeeManagementFilters");
        if (savedFilters) {
            try {
                const parsed = JSON.parse(savedFilters);
                if (parsed.filters) setFilters(parsed.filters);
                if (parsed.searchQuery) setSearchQuery(parsed.searchQuery);
            } catch (error) {
                console.error("Error parsing saved filters:", error);
            }
        }
        setFiltersInitialized(true);
    }, []);

    useEffect(() => {
        if (!filtersInitialized) return;

        const filtersToSave = {
            filters,
            searchQuery,
        };

        localStorage.setItem(
            "employeeManagementFilters",
            JSON.stringify(filtersToSave)
        );
    }, [filters, searchQuery, filtersInitialized]);

    const formatDate = (dateValue) => {
        if (!dateValue) return "";

        try {
            const parsedDate =
                typeof dateValue === "string" ? parseISO(dateValue) : new Date(dateValue);

            if (!isValid(parsedDate)) return "";

            return format(parsedDate, "dd-MM-yyyy");
        } catch (error) {
            return "";
        }
    };

    const fetchEmployees = useCallback(
        async (pageNumber = 1, searchTerm = "", customFilters = {}) => {
            setLoading(true);
            try {
                const apiUrl = `${API_BASE_URL}/employees/get-all`;

                const params = new URLSearchParams({
                    page: String(pageNumber),
                    pageSize: String(pageSize),
                    search: searchTerm,
                    sortBy: "id",
                    sortOrder: "asc",
                    filters: JSON.stringify(customFilters || {}),
                });

                const response = await axios.get(`${apiUrl}?${params.toString()}`, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });

                if (response.data?.status?.toLowerCase() === "ok") {
                    const rows = (response.data?.data || []).map((employee) => ({
                        id: employee.id,
                        serialNumber: employee.id,
                        employeeId: employee.employeeId || "",
                        name: employee.name || "",
                        email: employee.email || "",
                        designation: employee.designation || "",
                        region: Array.isArray(employee.region) ? employee.region.join(", ") : "",
                        department: employee.department || "",
                        mobile: employee.mobile || "",
                        reportingTo: employee.reportingTo || "",
                        dateOfJoining: employee.dateOfJoining || "",
                        isactive: employee.isactive,
                        isTerminated: employee.isTerminated,
                        status: employee.isTerminated
                            ? "Terminated"
                            : employee.isactive
                                ? "Active"
                                : "Inactive",
                        rawData: employee,
                    }));

                    setEmployees(rows);
                    setTotalPages(response.data?.pagination?.totalPages || 1);
                    setTotal(response.data?.pagination?.totalItems || 0);
                }
            } catch (err) {
                console.error("Error fetching employees:", err);
                Swal.fire({
                    title: t("Error"),
                    text: err.response?.data?.message || t("Failed to fetch employees"),
                    icon: "error",
                    confirmButtonText: t("OK"),
                });
            } finally {
                setLoading(false);
            }
        },
        [API_BASE_URL, pageSize, token, t]
    );

    useEffect(() => {
        if (!filtersInitialized || !user) return;
        fetchEmployees(page, searchQuery, filters);
    }, [page, searchQuery, filters, user, filtersInitialized, fetchEmployees]);

    const handleSearch = (searchTerm) => {
        setSearchQuery(searchTerm);
        setPage(1);
    };

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1);
        setFilterAnchor(null);
    };

    const handleColumnVisibilityChange = (newModel) => {
        setColumnVisibilityModel(newModel);
    };

    const handleOpenResetModal = (row) => {
        setSelectedEmployee(row);
        setNewPassword("");
        setConfirmPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setPasswordError("");
        setResetModalOpen(true);
    };

    const handleCloseResetModal = () => {
        if (resetLoading) return;
        setResetModalOpen(false);
        setSelectedEmployee(null);
        setNewPassword("");
        setConfirmPassword("");
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setPasswordError("");
    };

    const handleResetPasswordSubmit = async () => {
        setPasswordError("");

        if (!selectedEmployee?.email) {
            setPasswordError(t("Employee email is missing"));
            return;
        }

        if (!newPassword.trim() || !confirmPassword.trim()) {
            setPasswordError(t("Please enter password and confirm password"));
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError(t("Password and confirm password do not match"));
            return;
        }

        setResetLoading(true);

        try {
            const apiUrl = `${API_BASE_URL}/user/employee-reset-password`;

            const payload = {
                email: selectedEmployee.email,
                newPassword: newPassword,
            };

            const response = await axios.post(apiUrl, payload, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            const apiStatus = response?.data?.status?.toLowerCase();

            if (apiStatus === "success" || apiStatus === "ok") {
                handleCloseResetModal();
                Swal.fire({
                    title: t("Success"),
                    text: response?.data?.message || t("Password reset successfully"),
                    icon: "success",
                    showConfirmButton: false,
                    timer: 1200,
                });

            } else {
                Swal.fire({
                    title: t("Error"),
                    text: response?.data?.message || t("Failed to reset password"),
                    icon: "error",
                    confirmButtonText: t("OK"),
                });
            }
        } catch (err) {
            console.error("Error resetting password:", err);

            Swal.fire({
                title: t("Error"),
                text: err.response?.data?.message || t("Failed to reset password"),
                icon: "error",
                confirmButtonText: t("OK"),
            });
        } finally {
            setResetLoading(false);
        }
    };

    const columns = [
        {
            field: "serialNumber",
            headerName: t("ID"),
            width: 90,
            searchable: false,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "employeeId",
            headerName: t("Employee ID"),
            flex: 1,
            minWidth: 130,
            searchable: true,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "name",
            headerName: t("Name"),
            flex: 1.5,
            minWidth: 200,
            searchable: true,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "email",
            headerName: t("Email"),
            flex: 1.6,
            minWidth: 220,
            searchable: true,
            include: true,
            align: "left",
            headerAlign: "center",
        },
        {
            field: "designation",
            headerName: t("Role"),
            flex: 1.2,
            minWidth: 170,
            searchable: true,
            include: true,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "department",
            headerName: t("Department"),
            flex: 1,
            minWidth: 140,
            searchable: true,
            include: true,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "region",
            headerName: t("Region"),
            flex: 1,
            minWidth: 150,
            searchable: true,
            include: true,
            align: "center",
            headerAlign: "center",
        },
        {
            field: "dateOfJoining",
            headerName: t("Date of Joining"),
            flex: 1,
            minWidth: 150,
            searchable: false,
            include: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => formatDate(params.row.dateOfJoining) || "-",
        },
        {
            field: "status",
            headerName: t("Status"),
            flex: 1,
            minWidth: 130,
            searchable: true,
            include: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => {
                const status = params.row.status;
                const color =
                    status === "Active"
                        ? "#027a48"
                        : status === "Inactive"
                            ? "#b54708"
                            : "#b42318";

                return (
                    <span
                        style={{
                            padding: "4px 10px",
                            fontSize: "12px",
                            fontWeight: 600,
                            color,
                        }}
                    >
                        {t(status)}
                    </span>
                );
            },
        },
        {
            field: "action",
            headerName: t("Action"),
            flex: 1,
            minWidth: 170,
            sortable: false,
            searchable: false,
            include: true,
            align: "center",
            headerAlign: "center",
            renderCell: (params) => (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleOpenResetModal(params.row);
                    }}
                    style={{
                        padding: "8px 12px",
                        backgroundColor: "#01594C",
                        color: "#fff",
                        border: "none",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: 600,
                    }}
                    title={t("Reset Password")}
                >
                    {t("Reset Password")}
                </button>
            ),
        },
    ];

    const visibleColumns = columns.filter((col) => col.include !== false);
    const searchableFields = visibleColumns
        .filter((item) => item.searchable)
        .map((item) => item.field);
    const filteredData = visibleColumns.filter((item) =>
        searchableFields.includes(item.field)
    );

    const columnsToDisplay = {
        employeeId: "Employee ID",
        name: "Name",
        email: "Email",
        designation: "Designation",
        department: "Department",
        region: "Region",
        status: "Status",
    };

    return (
        <Sidebar title={t("Employee Management")}>
            <div className="orders-content">
                {loading && <LoadingSpinner />}

                <div className="table-container">
                    <div
                        style={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <DataGrid
                            apiRef={gridApiRef}
                            rows={employees}
                            columns={visibleColumns}
                            pageSize={pageSize}
                            rowCount={total}
                            disableRowSelectionOnClick
                            disableColumnMenu
                            hideFooter={true}
                            hideFooterPagination={true}
                            paginationMode="server"
                            rowHeight={62}
                            showToolbar
                            columnVisibilityModel={columnVisibilityModel}
                            onColumnVisibilityModelChange={handleColumnVisibilityChange}
                            slots={{
                                toolbar: () => (
                                    <CustomToolbar
                                        searchQuery={searchQuery}
                                        filterAnchor={filterAnchor}
                                        onSearch={handleSearch}
                                        setSearchQuery={setSearchQuery}
                                        setFilterAnchor={setFilterAnchor}
                                        handleFilterChange={handleFilterChange}
                                        onColumnVisibilityChange={setColumnVisibilityModel}
                                        columns={filteredData}
                                        filters={filters}
                                        columnVisibilityModel={columnVisibilityModel}
                                        searchPlaceholder={t("Search employees...")}
                                        showColumnVisibility={false}
                                        showFilters={true}
                                        showCalendar={false}
                                        showExport={false}
                                        showUpload={false}
                                        showAdd={false}
                                        showApproval={false}
                                        columnsToDisplay={columnsToDisplay}
                                        excludeFiltersFromChips={[]}
                                    />
                                ),
                            }}
                            sx={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                "& .MuiDataGrid-toolbar": {
                                    padding: "0px 8px !important",
                                    minHeight: "56px !important",
                                    flexShrink: 0,
                                },
                                "& .MuiDataGrid-main": {
                                    flex: 1,
                                    overflow: "hidden",
                                    display: "flex",
                                    flexDirection: "column",
                                },
                                "& .MuiDataGrid-virtualScroller": {
                                    overflow: "auto !important",
                                    flex: 1,
                                },
                                "& .MuiDataGrid-columnHeaders": {
                                    top: 0,
                                    zIndex: 1,
                                    backgroundColor: "white",
                                    borderBottom: "1px solid #e0e0e0",
                                    flexShrink: 0,
                                },
                                "& .MuiDataGrid-row": {
                                    cursor: "default",
                                },
                                "& .MuiDataGrid-cell": {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    textAlign: "center",
                                },
                                "& .MuiDataGrid-columnHeader": {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                },
                                "& .MuiDataGrid-columnHeaderTitleContainer": {
                                    justifyContent: "center",
                                },
                                "& .MuiDataGrid-columnHeaderTitle": {
                                    width: "100%",
                                    textAlign: "center",
                                },
                                "& .MuiDataGrid-cellContent": {
                                    width: "100%",
                                    textAlign: "center",
                                },
                                "& .MuiDataGrid-row:hover": {
                                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                                },
                            }}
                        />
                    </div>
                </div>

                {employees.length > 0 && (
                    <Pagination
                        currentPage={page}
                        totalPages={String(totalPages)}
                        onPageChange={setPage}
                    />
                )}

                <Dialog
                    open={resetModalOpen}
                    onClose={handleCloseResetModal}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle>{t("Reset Password")}</DialogTitle>

                    <DialogContent>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                mt: 1,
                            }}
                        >
                            <Typography variant="body2">
                                <strong>{t("Employee ID")}:</strong>{" "}
                                {selectedEmployee?.employeeId || "-"}
                            </Typography>

                            <Typography variant="body2">
                                <strong>{t("Name")}:</strong>{" "}
                                {selectedEmployee?.name || "-"}
                            </Typography>

                            <Typography variant="body2">
                                <strong>{t("Date of Joining")}:</strong>{" "}
                                {selectedEmployee?.dateOfJoining
                                    ? formatDate(selectedEmployee.dateOfJoining)
                                    : "-"}
                            </Typography>

                            <Typography variant="body2">
                                <strong>{t("Email")}:</strong>{" "}
                                {selectedEmployee?.email || "-"}
                            </Typography>

                            <TextField
                                label={t("Enter Password")}
                                type={showNewPassword ? "text" : "password"}
                                value={newPassword}
                                onChange={(e) => {
                                    setNewPassword(e.target.value);
                                    setPasswordError("");
                                }}
                                fullWidth
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowNewPassword((prev) => !prev)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                edge="end"
                                            >
                                                {showNewPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                label={t("Confirm Password")}
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => {
                                    setConfirmPassword(e.target.value);
                                    setPasswordError("");
                                }}
                                fullWidth
                                size="small"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                                                onMouseDown={(e) => e.preventDefault()}
                                                edge="end"
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {passwordError && (
                                <span
                                    style={{
                                        color: "red",
                                        fontSize: "13px",
                                        marginTop: "-6px",
                                    }}
                                >
                                    {passwordError}
                                </span>
                            )}
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ padding: "16px 24px" }}>
                        <Button onClick={handleCloseResetModal} disabled={resetLoading}>
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleResetPasswordSubmit}
                            disabled={resetLoading}
                            sx={{
                                backgroundColor: "#01594C",
                                "&:hover": { backgroundColor: "#014839" },
                            }}
                        >
                            {resetLoading ? t("Submitting...") : t("Submit")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        </Sidebar>
    );
}

export default EmployeeManagement;